import { useEffect, useState } from 'react';

import { ApiError, type Invite } from '../services/api/client';

/**
 * Painel de convite: QR Code para projetar em sala e link para copiar.
 *
 * O SVG vem pronto do backend — quem gera o QR é o servidor, então o token
 * nunca precisa ser manipulado pelo JavaScript da página.
 *
 * Validade de 30 min (PRD 33.3): o painel mostra o prazo restante e recalcula
 * a cada minuto, sem precisar recarregar. Cancelar e gerar outro são ações
 * distintas — cancelar não troca o token, só o invalida na hora.
 */
export function InvitePanel({
  title,
  description,
  load,
  rotate,
  cancel,
}: {
  title: string;
  description: string;
  load: () => Promise<Invite>;
  rotate?: () => Promise<Invite>;
  cancel?: () => Promise<void>;
}) {
  const [invite, setInvite] = useState<Invite | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    load()
      .then(setInvite)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Falha ao carregar o convite.'),
      );
    // `load` é recriada a cada render do pai; depender dela causaria laço.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  async function handleCopy() {
    if (!invite) return;

    await navigator.clipboard.writeText(invite.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleRotate() {
    if (!rotate) return;

    const confirmed = window.confirm(
      'Gerar um novo QR Code invalida o anterior na hora. ' +
        'Quem já entrou continua matriculado. Continuar?',
    );

    if (!confirmed) return;

    setRotating(true);

    try {
      setInvite(await rotate());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao gerar novo convite.');
    } finally {
      setRotating(false);
    }
  }

  async function handleCancel() {
    if (!cancel || !invite) return;

    const confirmed = window.confirm(
      'Cancelar invalida este QR Code na hora. Quem já entrou continua matriculado. Continuar?',
    );

    if (!confirmed) return;

    setCancelling(true);

    try {
      await cancel();
      setInvite({ ...invite, expiresAt: new Date(0).toISOString() });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao cancelar o convite.');
    } finally {
      setCancelling(false);
    }
  }

  const remainingMs = invite?.expiresAt ? new Date(invite.expiresAt).getTime() - now : null;
  const expired = remainingMs !== null && remainingMs <= 0;
  const remainingMinutes = remainingMs !== null ? Math.max(0, Math.ceil(remainingMs / 60_000)) : null;

  return (
    <section className="card">
      <h3>{title}</h3>
      <p className="muted">{description}</p>

      {error && <p className="form-error">{error}</p>}
      {!invite && !error && <p className="muted">Gerando…</p>}

      {invite && (
        <div className="invite-body">
          <div
            className="qr-code"
            // O SVG é gerado pelo nosso próprio backend a partir de uma URL
            // que nós montamos — não há conteúdo de terceiros aqui.
            dangerouslySetInnerHTML={{ __html: invite.qrCodeSvg }}
          />

          <div className="invite-actions">
            <label htmlFor="invite-url">Link</label>
            <input
              id="invite-url"
              readOnly
              value={invite.url}
              disabled={expired}
              onFocus={(e) => e.target.select()}
            />

            <p className={expired ? 'form-error small' : 'muted small'} role="status">
              {expired
                ? 'Expirado — gere um novo QR Code.'
                : `Expira em ${remainingMinutes} min.`}
            </p>

            <div className="button-row">
              <button type="button" onClick={() => void handleCopy()} disabled={expired}>
                {copied ? 'Copiado' : 'Copiar link'}
              </button>

              {rotate && (
                <button type="button" onClick={() => void handleRotate()} disabled={rotating}>
                  {rotating ? 'Gerando…' : 'Gerar novo'}
                </button>
              )}

              {cancel && !expired && (
                <button type="button" onClick={() => void handleCancel()} disabled={cancelling}>
                  {cancelling ? 'Cancelando…' : 'Cancelar convite'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
